import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { PrecounselingQuestionnaireAnswersControllerCreateRequest } from '@doda/common/src/types/precounseling';

export type ScreenName = 'top' | 'question' | 'edit' | 'complete' | 'error';

export type QuestionTitle =
  | 'JobChangeStatus'
  | 'JobChangeTiming'
  | 'DesiredIndustry'
  | 'DesiredJobType'
  | 'DesiredArea'
  | 'DesiredIncome'
  | 'JobChangeReason'
  | 'CounselingConsultation'
  | 'ConsideringCompany'
  | 'Confirmation';

// PC/SPページ共通の状態管理（例：選択中のタブを管理する…みたいなuiの状態管理）

/**
 * プレカウンセリングページ
 * @param screenName 表示する画面名
 * @param prevScreenName 直前の画面名
 * @param questionTitle 表示する設問項目名
 * @param answers 回答内容
 * @param validationError バリデーションチェック
 */
export interface PrecounselingState {
  hraAnswersStatus: boolean;
  screenName: ScreenName;
  prevScreenName: ScreenName;
  questionTitle: QuestionTitle;
  answers: PrecounselingQuestionnaireAnswersControllerCreateRequest;
  validationError: boolean;
  questionsOpenedStatus: {
    jobChangeStatus: boolean;
    jobChangeTiming: boolean;
    desiredArea: boolean;
    desiredIncome: boolean;
    jobChangeReason: boolean;
    counselingConsultation: boolean;
  };
  selectBoxCheckStatus: {
    counselingConsultationNothingInParticular: boolean;
    counselingConsultationOther: boolean;
  };
  hasNoConsideringCompany: boolean;
}

export const precounselingInitialState: PrecounselingState = {
  hraAnswersStatus: true,
  screenName: 'top',
  prevScreenName: 'top',
  questionTitle: 'JobChangeStatus',
  answers: {
    jobChangeStatus: {
      jobChangeStatus: '1',
      remark: '',
    },
    jobChangeTiming: {
      jobChangeTiming: '1',
      desiredJobChangeYear: -1, // 「値が'0'でない場合」でバリデーションをかけているため、初期描画時にエラーにならないように'-1'を設定する
      desiredJobChangeMonth: -1, // 「値が'0'でない場合」でバリデーションをかけているため、初期描画時にエラーにならないように'-1'を設定する
      remark: '',
    },
    desiredIndustry: {
      list: [],
      remark: '',
    },
    desiredJobType: {
      list: [],
      remark: '',
    },
    desiredArea: {
      transferAvailability: '1',
      list: [],
      remark: '',
    },
    desiredIncome: {
      desiredIncome: '1',
      desiredMinimumIncome: -1, // 「値が'0'でない場合」でバリデーションをかけているため、初期描画時にエラーにならないように'-1'を設定する
      remark: '',
    },
    jobChangeReason: {
      list: [],
      other: '',
      remark: '',
    },
    counselingConsultation: {
      list: [],
      other: '',
    },
    consideringCompany: {
      list: [],
      remark: '',
    },
  },
  validationError: true,
  questionsOpenedStatus: {
    jobChangeStatus: false,
    jobChangeTiming: false,
    desiredArea: false,
    desiredIncome: false,
    jobChangeReason: false,
    counselingConsultation: false,
  },
  selectBoxCheckStatus: {
    counselingConsultationNothingInParticular: false,
    counselingConsultationOther: false,
  },
  hasNoConsideringCompany: false,
};

const precounselingSlice = createSlice({
  name: 'precounseling',
  initialState: precounselingInitialState,
  reducers: {
    setHraAnswersStatus: (state, action: PayloadAction<boolean>) => {
      state.hraAnswersStatus = action.payload;
    },
    setScreenName: (state, action: PayloadAction<ScreenName>) => {
      state.screenName = action.payload;
    },
    setPrevScreenName: (state, action: PayloadAction<ScreenName>) => {
      state.prevScreenName = action.payload;
    },
    setQuestionTitle: (state, action: PayloadAction<QuestionTitle>) => {
      state.questionTitle = action.payload;
    },
    setJobChangeStatus: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['jobChangeStatus']
      >
    ) => {
      state.answers.jobChangeStatus = action.payload;
    },
    setJobChangeTiming: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['jobChangeTiming']
      >
    ) => {
      state.answers.jobChangeTiming = action.payload;
    },
    setDesiredIndustry: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['desiredIndustry']
      >
    ) => {
      state.answers.desiredIndustry = action.payload;
    },
    setDesiredJobType: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['desiredJobType']
      >
    ) => {
      state.answers.desiredJobType = action.payload;
    },
    setDesiredArea: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['desiredArea']
      >
    ) => {
      state.answers.desiredArea = action.payload;
    },
    setDesiredIncome: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['desiredIncome']
      >
    ) => {
      state.answers.desiredIncome = action.payload;
    },
    setJobChangeReason: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['jobChangeReason']
      >
    ) => {
      state.answers.jobChangeReason = action.payload;
    },
    setCounselingConsultation: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['counselingConsultation']
      >
    ) => {
      state.answers.counselingConsultation = action.payload;
    },
    setValidationError: (state, action: PayloadAction<boolean>) => {
      state.validationError = action.payload;
    },
    setJobChangeStatusOpenedStatus: (state, action: PayloadAction<boolean>) => {
      state.questionsOpenedStatus.jobChangeStatus = action.payload;
    },
    setJobChangeTimingOpenedStatus: (state, action: PayloadAction<boolean>) => {
      state.questionsOpenedStatus.jobChangeTiming = action.payload;
    },
    setDesiredAreaOpenedStatus: (state, action: PayloadAction<boolean>) => {
      state.questionsOpenedStatus.desiredArea = action.payload;
    },
    setDesiredIncomeOpenedStatus: (state, action: PayloadAction<boolean>) => {
      state.questionsOpenedStatus.desiredIncome = action.payload;
    },
    setJobChangeReasonOpenedStatus: (state, action: PayloadAction<boolean>) => {
      state.questionsOpenedStatus.jobChangeReason = action.payload;
    },
    setCounselingConsultationOpenedStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.questionsOpenedStatus.counselingConsultation = action.payload;
    },
    setCounselingConsultationNothingSelectBoxCheckStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.selectBoxCheckStatus.counselingConsultationNothingInParticular =
        action.payload;
    },
    setCounselingConsultationOtherSelectBoxCheckStatus: (
      state,
      action: PayloadAction<boolean>
    ) => {
      state.selectBoxCheckStatus.counselingConsultationOther = action.payload;
    },
    setConsideringCompany: (
      state,
      action: PayloadAction<
        PrecounselingQuestionnaireAnswersControllerCreateRequest['consideringCompany']
      >
    ) => {
      state.answers.consideringCompany = action.payload;
    },
    setHasNoConsideringCompany: (state, action: PayloadAction<boolean>) => {
      state.hasNoConsideringCompany = action.payload;
    },
  },
});

// Create Action
export const {
  setHraAnswersStatus,
  setScreenName,
  setPrevScreenName,
  setQuestionTitle,
  setJobChangeStatus,
  setJobChangeTiming,
  setDesiredIndustry,
  setDesiredJobType,
  setDesiredArea,
  setDesiredIncome,
  setJobChangeReason,
  setCounselingConsultation,
  setValidationError,
  setJobChangeStatusOpenedStatus,
  setJobChangeTimingOpenedStatus,
  setDesiredAreaOpenedStatus,
  setDesiredIncomeOpenedStatus,
  setJobChangeReasonOpenedStatus,
  setCounselingConsultationOpenedStatus,
  setCounselingConsultationNothingSelectBoxCheckStatus,
  setCounselingConsultationOtherSelectBoxCheckStatus,
  setConsideringCompany,
  setHasNoConsideringCompany,
} = precounselingSlice.actions;

// Reducer
export const precounselingReducer = precounselingSlice.reducer;

// Selector

interface State {
  precounseling: PrecounselingState;
}

/**
 * 診断アンケート回答状況取得セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 診断アンケート回答状況取得
 */
export const hraAnswersStatusSelector = <S extends State>(state: S) =>
  state.precounseling.hraAnswersStatus;

/**
 * 表示する画面名セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 表示する画面名
 */
export const screenNameSelector = <S extends State>(state: S) =>
  state.precounseling.screenName;

/**
 * 直前の画面名セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 直前の画面名
 */
export const prevScreenNameSelector = <S extends State>(state: S) =>
  state.precounseling.prevScreenName;

/**
 * 表示する設問項目名セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 表示する設問項目名
 */
export const questionTitleSelector = <S extends State>(state: S) =>
  state.precounseling.questionTitle;

/**
 * 全ての回答結果セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 全ての回答結果
 */
export const answersSelector = <S extends State>(state: S) =>
  state.precounseling.answers;

/**
 * 回答結果_Q1（転職ステータス）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q1（転職ステータス）
 */
export const jobChangeStatusSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.jobChangeStatus;

/**
 * 回答結果_Q2（転職タイミング）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q2（転職タイミング）
 */
export const jobChangeTimingSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.jobChangeTiming;

/**
 * 回答結果_Q3（希望業種）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q3（希望業種）
 */
export const desiredIndustrySelector = <S extends State>(state: S) =>
  state.precounseling.answers?.desiredIndustry;

/**
 * 回答結果_Q4（希望職種）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q4（希望職種）
 */
export const desiredJobTypeSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.desiredJobType;

/**
 * 回答結果_Q5（希望勤務地）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q5（希望勤務地）
 */
export const desiredAreaSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.desiredArea;

/**
 * 回答結果_Q6（希望年収）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q6（希望年収）
 */
export const desiredIncomeSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.desiredIncome;

/**
 * 回答結果_Q7（転職理由）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q7（転職理由）
 */
export const jobChangeReasonSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.jobChangeReason;

/**
 * 回答結果_Q8（相談事項）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q8（相談事項）
 */
export const counselingConsultationSelector = <S extends State>(state: S) =>
  state.precounseling.answers?.counselingConsultation;

/**
 * バリデーションチェック結果セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns バリデーションチェック結果
 */
export const validationErrorSelector = <S extends State>(state: S) =>
  state.precounseling.validationError;

/**
 * 対象画面を開いたかどうかセレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 対象画面を開いたかどうか
 */
export const questionsOpenedStatusSelector = <S extends State>(state: S) =>
  state.precounseling.questionsOpenedStatus;

/**
 * セレクトボックス選択状態セレクタ
 * 回答送信対象外のチェックボックスの状態を管理する
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns セレクトボックス選択状態
 */
export const selectBoxCheckStatusSelector = <S extends State>(state: S) =>
  state.precounseling.selectBoxCheckStatus;

/**
 * 回答結果_Q9（検討中の企業）セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 回答結果_Q9（検討中の企業）
 */
export const consideringCompanySelector = <S extends State>(state: S) =>
  state.precounseling.answers?.consideringCompany;

/**
 * 検討中の企業の有無セレクタ
 *
 * @param state プレカウンセリングページで更新されたUIの状態を管理するオブジェクト
 * @returns 検討中の企業の有無
 */
export const hasNoConsideringCompanySelector = <S extends State>(state: S) =>
  state.precounseling.hasNoConsideringCompany;
